/**
 * Parent (Service Intel) ↔ iframe bridge logic.
 *
 * The parent sends `{ type: 'usr', data: {...} }` via postMessage. This used to
 * be handled ONCE in <Embed> and then ignored — so a later change (e.g. the
 * parent switching nacCampaignId, or sending 0 to start a NEW campaign) was
 * never picked up, and the iframe stayed stuck on the first/cached value.
 *
 * Now the listener lives in <ParentBridge> (mounted app-wide, survives route
 * changes) and re-routes whenever the campaign/shop actually changes. This
 * module holds the shared, testable pieces.
 */
import { shopService } from '../services/index.js';

export const ALLOWED_ORIGINS = [
  'http://localhost:34188',
  'https://app.service-intel.com',
  'https://si.siauto.pro',
  'https://app.siauto.pro',
  'https://dev.siauto.pro',
];

const log = (...args) => console.log('[postcards/bridge]', ...args);
const warn = (...args) => console.warn('[postcards/bridge]', ...args);

// Last origin a valid parent message came from, so the iframe can post BACK to
// the parent (e.g. search-results) targeting the exact origin instead of '*'.
let lastParentOrigin = null;
export const setParentOrigin = (origin) => {
  if (origin) {
    lastParentOrigin = origin;
    try {
      localStorage.setItem('parentOrigin', origin);
    } catch {
      /* ignore */
    }
  }
};
export const getParentOrigin = () => {
  if (lastParentOrigin) return lastParentOrigin;
  try {
    return localStorage.getItem('parentOrigin') || null;
  } catch {
    return null;
  }
};

/**
 * Resolve the SI AppHost from the iframe's own localStorage. Prefers the
 * dedicated `appHost` key (written by persistUsr), but falls back to reading
 * `AppHost` out of the persisted `usr` payload — SI reliably shares `usr`, and
 * some code paths only ever wrote `usr`, so this avoids an empty appHost.
 */
export const getAppHost = () => {
  try {
    const direct = localStorage.getItem('appHost');
    if (direct) return direct;
    const usrRaw = localStorage.getItem('usr');
    if (usrRaw) {
      const usr = JSON.parse(usrRaw);
      const fromUsr = usr?.AppHost || usr?.appHost || '';
      if (fromUsr) {
        // Self-heal the dedicated key so later reads are cheap/consistent.
        try {
          localStorage.setItem('appHost', fromUsr);
        } catch {
          /* ignore */
        }
        return fromUsr;
      }
    }
  } catch {
    /* ignore */
  }
  return '';
};

/**
 * True when the app runs inside an iframe (the Service Intel embed). Used to
 * hide the in-app titles/headers, since the parent already shows that context.
 * A cross-origin access throws → treat as embedded.
 */
export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/**
 * Post a message from the iframe back UP to the parent (Service Intel),
 * targeting the known parent origin when possible (falls back to '*').
 */
export const postToParent = (message) => {
  if (typeof window === 'undefined' || window.parent === window) return;
  const target = getParentOrigin() || '*';
  try {
    window.parent.postMessage(message, target);
  } catch (err) {
    warn('postToParent failed', err.message);
  }
};

// Find New Customer panel params the parent may pass to pre-fill / auto-run
// the search (see campaign-params-api.md §4). Address type is always SFH.
// NOTE: sorting is NOT a param — results are always sorted by distance
// ascending (nearest first). See FIXED_SORT in ShopDetail/CampaignMap.
const CAMPAIGN_PARAM_KEYS = [
  'mode',
  'radiusMiles',
  'searchZipCode',
  'searchCity',
  'searchState',
  'searchRadius',
  'maxRecords',
  'zipCodes',
  'excludeZipCodes',
  'cities',
  'excludeCities',
  'includeUsed',
  'campaignName',
  'autoRun',
];

export const collectCampaignParamsFromQuery = (params) => {
  const out = {};
  for (const key of CAMPAIGN_PARAM_KEYS) {
    const v = params.get(key);
    if (v != null && v !== '') out[key] = v;
  }
  return out;
};

// --- Add-to-campaign (run-search) requests from the parent -----------------
// SI drives "add new addresses to this campaign" by re-sending the `usr`
// payload with a `campaignParams` object while `view:'campaign'` +
// `CampaignId > 0` (see ADD_TO_CAMPAIGN_API.md §6). <ParentBridge> normalizes
// those params and dispatches a `postcards:add-to-campaign` CustomEvent that
// CampaignMap consumes. But the FIRST such message can arrive before
// CampaignMap is mounted (it triggers navigation to /campaign/:id), so we also
// stash the request here; CampaignMap drains it on mount. Each request carries
// a monotonically increasing token so it runs exactly once (live listener OR
// drain, never both).
let addRequestToken = 0;
let pendingCampaignAdd = null; // { campaignId, params, token }

export const nextAddRequestToken = () => ++addRequestToken;

export const enqueuePendingCampaignAdd = (request) => {
  pendingCampaignAdd = request;
};

/** Consume the buffered add request IF it targets `campaignId`. One-shot. */
export const consumePendingCampaignAdd = (campaignId) => {
  if (!pendingCampaignAdd) return null;
  if (Number(pendingCampaignAdd.campaignId) !== Number(campaignId)) return null;
  const req = pendingCampaignAdd;
  pendingCampaignAdd = null;
  return req;
};

/**
 * Build the canonical add-to-campaign override object from a parent's
 * `campaignParams`. Accepts the field names SI sends (mode, maxRecords,
 * radiusMiles, includeApartments/onlyApartments, campaignName) plus a few
 * aliases. Kept here so the contract lives in one place.
 * Sorting is deliberately NOT accepted: it is always distance ascending.
 */
export const normalizeCampaignParams = (cp = {}) => {
  const num = (v) => (v == null || v === '' ? null : Number(v));
  const bool = (v) => v === true || v === 'true' || v === 1 || v === '1';
  return {
    mode: cp.mode === 'location' ? 'location' : cp.mode === 'radius' ? 'radius' : null,
    maxRecords: num(cp.maxRecords ?? cp.count ?? cp.records),
    // Search Radius — drives the search AND the coverage circle. SI sends
    // `radiusMiles`; accept `searchRadius` too.
    searchRadius: num(cp.searchRadius ?? cp.radiusMiles ?? cp.radius),
    includeApartments: cp.includeApartments != null ? bool(cp.includeApartments) : null,
    onlyApartments: cp.onlyApartments != null ? bool(cp.onlyApartments) : null,
    addressType: cp.addressType ?? null,
    campaignName: cp.campaignName ?? null,
    // Optional explicit center (forces a location search, no geocoding).
    latitude: cp.latitude != null ? Number(cp.latitude) : null,
    longitude: cp.longitude != null ? Number(cp.longitude) : null,
    searchZipCode: cp.searchZipCode ?? cp.zipCode ?? '',
    searchCity: cp.searchCity ?? cp.city ?? '',
    searchState: cp.searchState ?? cp.state ?? '',
    includeUsed: bool(cp.includeUsed),
  };
};

/**
 * Normalize an NCA campaign id. Returns a POSITIVE integer, or `null` for
 * 0 / "0" / "" / null / undefined / negative / NaN.
 *
 * A null result means "no existing campaign" → the parent wants to CREATE a new
 * one, so we route to the shop, NOT to a (stale) campaign map. This is the core
 * fix: previously the string "0" was truthy and routed to `/campaign/0`.
 */
export const parseCampaignId = (raw) => {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
};

/** Persist the latest parent payload so reloads keep working. */
export const persistUsr = (usrData) => {
  try {
    localStorage.setItem('usr', JSON.stringify(usrData));
    if (usrData?.AppHost) localStorage.setItem('appHost', usrData.AppHost);
  } catch (err) {
    warn('persist failed', err.message);
  }
};

// Dedup: the route key we last navigated to. Module-level so <Embed> (initial
// resolution) and <ParentBridge> (live updates) share it and don't fight.
let lastRouteKey = null;
export const resetRouteKey = () => {
  lastRouteKey = null;
};

/**
 * Resolve a parent `usr` payload into a navigation.
 *
 * - Valid nacCampaignId (> 0)  → /campaign/:id  (view an existing campaign)
 * - nacCampaignId 0 / absent   → resolve lid → /shops/:id  (create-new flow)
 *
 * Re-routes only when the target actually changes (deduped by route key), so
 * repeated identical messages don't cause redundant navigations, but a genuine
 * change (new campaign, switch to/from create-new) is applied live.
 *
 * @returns {Promise<string|null>} the route key navigated to, or null on no-op
 */
export const resolveAndRoute = async (usrData, navigate, opts = {}) => {
  const { onError = () => {}, onStatus = () => {}, force = false } = opts;

  const rawCampaign =
    usrData?.CampaignId ?? usrData?.NacCampaignId ?? usrData?.nacCampaignId;
  const campaignId = parseCampaignId(rawCampaign);

  // "Create new campaign" = a campaign id was explicitly sent but it's 0/invalid
  // (vs. no campaign field at all, which is a normal shop entry).
  const isCreateNew =
    rawCampaign != null && rawCampaign !== '' && campaignId === null;

  // Existing campaign → campaign map.
  if (campaignId) {
    const key = `campaign:${campaignId}`;
    if (!force && key === lastRouteKey) {
      log('campaign unchanged → skip', campaignId);
      return null;
    }
    lastRouteKey = key;
    log('→ /campaign', campaignId);
    navigate(`/campaign/${campaignId}`, { replace: true });
    return key;
  }

  // No (valid) campaign → "create new campaign" / normal shop flow.
  if (!usrData?.lid) {
    warn('no lid and no valid campaign → cannot route', usrData);
    onError('No location (lid) received from parent.');
    return null;
  }

  const key = `shop:${usrData.lid}`;
  if (!force && key === lastRouteKey) {
    log('shop unchanged → skip', usrData.lid);
    return null;
  }

  try {
    onStatus('Loading shop...');
    log('resolving shop by locationId', usrData.lid);
    const res = await shopService.getShopByLocationId(usrData.lid);
    const shop = res.data.shop;

    const search = new URLSearchParams(window.location.search);
    const campaignParams = {
      ...collectCampaignParamsFromQuery(search),
      ...(usrData.campaignParams || {}),
    };
    // Create-new flow forces the FULL shop view (panel visible) so the user can
    // actually build the campaign — view=campaign / mapOnly are ignored here.
    const view = isCreateNew
      ? undefined
      : search.get('view') ||
        usrData.view ||
        (search.get('mapOnly') === '1' ||
        search.get('panel') === 'hidden' ||
        usrData.mapOnly === true
          ? 'campaign'
          : undefined);
    if (isCreateNew) log('create-new flow → ignoring view=campaign, showing full panel');

    lastRouteKey = key;
    log('→ /shops', { shopId: shop._id, view: view || 'full' });
    navigate(`/shops/${shop._id}`, {
      replace: true,
      state: {
        usr: usrData,
        readOnly: usrData.ReadOnly === true,
        appHost: usrData.AppHost || '',
        ...(view ? { view } : {}),
        ...(Object.keys(campaignParams).length ? { campaignParams } : {}),
      },
    });
    return key;
  } catch (err) {
    warn('shop lookup failed', err.message);
    onError(err.message || `Shop with locationId ${usrData.lid} not found.`);
    return null;
  }
};
